/**
 * スマホ向け商品画像一覧モーダル
 * 一覧表示 → タップで拡大 → 前後ナビ（プリロード + キャッシュ）
 */
(function () {
  const MOBILE_MQL = window.matchMedia('(max-width: 749px)');
  const PRELOAD_CONCURRENCY = 4;
  const PRELOAD_AHEAD = 2;

  class ProductMobileGallery {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.imageCache = new Map();
      this.preloadStarted = false;
      this.viewerRequestId = 0;
      this.currentIndex = 0;
      this.items = [];

      this.trigger = root.querySelector('.product-mobile-gallery__trigger');
      this.listModal = root.querySelector('[data-gallery-list]');
      this.viewerModal = root.querySelector('[data-gallery-viewer]');
      this.viewerImage = root.querySelector('[data-viewer-image]');
      this.viewerLoader = root.querySelector('[data-viewer-loader]');
      this.viewerCounter = root.querySelector('[data-viewer-counter]');
      this.closeListBtn = root.querySelector('[data-close-list]');
      this.backToListBtn = root.querySelector('[data-back-to-list]');
      this.prevBtn = root.querySelector('[data-viewer-prev]');
      this.nextBtn = root.querySelector('[data-viewer-next]');
      this.gridButtons = root.querySelectorAll('[data-open-viewer]');

      this.syncItemsFromGrid();

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCloseList = this.onCloseList.bind(this);
      this.onBackToList = this.onBackToList.bind(this);
      this.onGridClick = this.onGridClick.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);

      this.touchStartX = 0;
      this.bindEvents();
    }

    syncItemsFromGrid() {
      this.items = Array.from(this.gridButtons).map((button) => {
        const thumbImg = button.querySelector('img');

        return {
          src: button.dataset.src || '',
          thumb: button.dataset.thumb || thumbImg?.currentSrc || thumbImg?.src || '',
          alt: button.dataset.alt || thumbImg?.alt || '',
        };
      });
    }

    bindEvents() {
      this.trigger?.addEventListener('click', this.onTriggerClick);
      this.closeListBtn?.addEventListener('click', this.onCloseList);
      this.backToListBtn?.addEventListener('click', this.onBackToList);
      this.prevBtn?.addEventListener('click', this.onPrev);
      this.nextBtn?.addEventListener('click', this.onNext);
      this.gridButtons.forEach((button) => {
        button.addEventListener('click', this.onGridClick);
      });
      this.viewerModal?.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.viewerModal?.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    isMobile() {
      return MOBILE_MQL.matches;
    }

    lockScroll() {
      document.body.classList.add('product-mobile-gallery--open');
    }

    unlockScroll() {
      document.body.classList.remove('product-mobile-gallery--open');
    }

    openModal(modal) {
      if (!modal) return;
      modal.hidden = false;
      document.addEventListener('keydown', this.onKeydown);
      this.lockScroll();
    }

    closeModal(modal) {
      if (!modal) return;
      modal.hidden = true;
      if (this.listModal?.hidden && this.viewerModal?.hidden) {
        document.removeEventListener('keydown', this.onKeydown);
        this.unlockScroll();
      }
    }

    preloadImage(src) {
      if (!src) return Promise.reject(new Error('Missing src'));

      if (this.imageCache.has(src)) {
        return this.imageCache.get(src);
      }

      const promise = new Promise((resolve, reject) => {
        const img = new Image();
        img.decoding = 'async';

        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load ${src}`));
        img.src = src;
      }).catch((error) => {
        this.imageCache.delete(src);
        throw error;
      });

      this.imageCache.set(src, promise);
      return promise;
    }

    preloadAroundIndex(index) {
      for (let offset = -PRELOAD_AHEAD; offset <= PRELOAD_AHEAD; offset += 1) {
        const targetIndex = index + offset;
        if (targetIndex < 0 || targetIndex >= this.items.length) continue;
        this.preloadImage(this.items[targetIndex].src);
      }
    }

    startBackgroundPreload() {
      if (this.preloadStarted || !this.items.length) return;
      this.preloadStarted = true;

      const preloadAll = async () => {
        for (let i = 0; i < this.items.length; i += PRELOAD_CONCURRENCY) {
          const chunk = this.items.slice(i, i + PRELOAD_CONCURRENCY);
          await Promise.all(chunk.map((item) => this.preloadImage(item.src).catch(() => {})));
        }
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => preloadAll(), { timeout: 2000 });
      } else {
        window.setTimeout(preloadAll, 300);
      }
    }

    setLoading(isLoading) {
      this.root.classList.toggle('is-loading', isLoading);
      if (this.viewerLoader) {
        this.viewerLoader.hidden = !isLoading;
      }
    }

    updateControls() {
      if (this.viewerCounter) {
        this.viewerCounter.textContent = `${this.currentIndex + 1} / ${this.items.length}`;
      }

      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
      }

      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex === this.items.length - 1;
      }
    }

    showImage(item, useThumb) {
      if (!this.viewerImage || !item) return;

      this.viewerImage.src = useThumb ? item.thumb || item.src : item.src;
      this.viewerImage.alt = item.alt || '';
    }

    async updateViewer(options) {
      const item = this.items[this.currentIndex];
      if (!item || !this.viewerImage) return;

      const requestId = ++this.viewerRequestId;
      const showThumbFirst = options?.showThumbFirst !== false;
      this.updateControls();
      this.preloadAroundIndex(this.currentIndex);

      if (showThumbFirst) {
        this.showImage(item, true);
      }

      this.setLoading(true);

      try {
        await this.preloadImage(item.src);

        if (requestId !== this.viewerRequestId) return;

        this.showImage(item, false);

        if (this.viewerImage.decode) {
          await this.viewerImage.decode().catch(() => {});
        }
      } catch (error) {
        if (requestId !== this.viewerRequestId) return;
        this.showImage(item, false);
      } finally {
        if (requestId === this.viewerRequestId) {
          this.setLoading(false);
        }
      }
    }

    onTriggerClick() {
      if (!this.isMobile()) return;
      this.syncItemsFromGrid();
      this.openModal(this.listModal);
      this.startBackgroundPreload();
      this.preloadAroundIndex(0);
      this.closeListBtn?.focus();
    }

    onCloseList() {
      this.viewerRequestId += 1;
      this.setLoading(false);
      this.closeModal(this.listModal);
      this.closeModal(this.viewerModal);
      this.trigger?.focus();
    }

    onBackToList() {
      this.viewerRequestId += 1;
      this.setLoading(false);
      this.closeModal(this.viewerModal);
      this.openModal(this.listModal);
    }

    onGridClick(event) {
      const button = event.currentTarget;
      const index = Number(button.dataset.index);
      if (Number.isNaN(index)) return;
      this.openViewer(index, button);
    }

    openViewer(index, button) {
      this.syncItemsFromGrid();

      const thumbImg = button?.querySelector('img');
      if (button && this.items[index]) {
        this.items[index] = {
          src: button.dataset.src || this.items[index].src,
          thumb: button.dataset.thumb || thumbImg?.currentSrc || thumbImg?.src || this.items[index].thumb,
          alt: button.dataset.alt || thumbImg?.alt || this.items[index].alt,
        };
      }

      this.currentIndex = index;
      this.updateControls();

      const item = this.items[index];
      if (item) {
        this.showImage(item, true);
      }

      this.setLoading(true);
      this.closeModal(this.listModal);
      this.openModal(this.viewerModal);
      this.backToListBtn?.focus();

      this.updateViewer({ showThumbFirst: false });
    }

    onPrev() {
      if (this.currentIndex <= 0) return;
      this.currentIndex -= 1;
      this.updateViewer();
    }

    onNext() {
      if (this.currentIndex >= this.items.length - 1) return;
      this.currentIndex += 1;
      this.updateViewer();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.viewerModal?.hidden) {
          this.onBackToList();
        } else if (!this.listModal?.hidden) {
          this.onCloseList();
        }
        return;
      }

      if (this.viewerModal?.hidden) return;

      if (event.key === 'ArrowLeft') {
        this.onPrev();
      }

      if (event.key === 'ArrowRight') {
        this.onNext();
      }
    }

    onTouchStart(event) {
      this.touchStartX = event.changedTouches[0]?.screenX || 0;
    }

    onTouchEnd(event) {
      const touchEndX = event.changedTouches[0]?.screenX || 0;
      const diff = touchEndX - this.touchStartX;

      if (Math.abs(diff) < 50) return;

      if (diff < 0) {
        this.onNext();
      } else {
        this.onPrev();
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-product-mobile-gallery]').forEach((root) => {
      new ProductMobileGallery(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
